import { Download, FileUp, LogOut, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const documentTypes = ["Resume", "White Card", "Driver Licence", "High Risk Ticket", "Trade Qualification"];

export default function CandidatePortalPage() {
  const { user, signOut } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const loadDocuments = () => fetch(`${API}/documents`, { credentials: "include" })
    .then((response) => response.ok ? response.json() : [])
    .then(setDocuments)
    .catch(() => setDocuments([]));

  useEffect(() => { loadDocuments(); }, []);

  const upload = async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    if (!form.get("file")?.name) {
      toast.error("Choose a document to upload.");
      return;
    }
    setUploading(true);
    try {
      const response = await fetch(`${API}/documents`, { method: "POST", credentials: "include", body: form });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Unable to upload document.");
      }
      const uploadedDocument = await response.json();
      setDocuments((current) => [uploadedDocument, ...current]);
      formElement.reset();
      toast.success("Your document has been stored securely.");
      window.setTimeout(loadDocuments, 800);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const download = async (fileRecord) => {
    try {
      const response = await fetch(`${API}/documents/${fileRecord.id}/download`, { credentials: "include" });
      if (!response.ok) throw new Error();
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = fileRecord.original_filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("This document is temporarily unavailable.");
    }
  };

  return (
    <main className="candidate-portal" data-testid="candidate-portal-page">
      <header className="portal-header">
        <div><p className="eyebrow">RIGHT HIRE PERSONNEL</p><h1>MY WORKFORCE PROFILE</h1></div>
        <div className="portal-actions"><span data-testid="candidate-portal-user">{user.name}</span><button type="button" onClick={signOut} data-testid="candidate-logout-button"><LogOut size={17} /> Sign out</button></div>
      </header>
      <section className="portal-body">
        <div className="portal-intro"><ShieldCheck size={25} /><div><p className="eyebrow">PRIVATE DOCUMENT CENTRE</p><h2>KEEP YOUR RECORDS READY.</h2><p>Upload current work documents so the Right Hire team can put you forward confidently.</p></div></div>
        <div className="portal-grid">
          <form className="document-form" onSubmit={upload} data-testid="document-upload-form">
            <p className="form-subtitle">UPLOAD A DOCUMENT</p>
            <label className="field-wrap">Document type<select className="field-input" name="document_type" data-testid="document-type-select">{documentTypes.map((type) => <option key={type}>{type}</option>)}</select></label>
            <label className="document-picker" data-testid="document-file-picker"><FileUp size={22} /><span><b>Select a PDF, JPG or PNG</b><small>Maximum file size: 10 MB</small></span><input type="file" name="file" accept=".pdf,.jpg,.jpeg,.png" data-testid="document-file-input" /></label>
            <button type="submit" className="button-primary form-submit" disabled={uploading} data-testid="document-upload-submit-button">{uploading ? "Uploading securely…" : "Upload document"}<FileUp size={17} /></button>
          </form>
          <section className="document-list" data-testid="candidate-document-list"><p className="form-subtitle">MY STORED DOCUMENTS</p>{documents.length ? documents.map((item) => <article key={item.id} data-testid={`candidate-document-${item.id}`}><div><strong>{item.document_type}</strong><span>{item.original_filename} · {Math.ceil(item.size / 1024)} KB</span></div><button type="button" onClick={() => download(item)} data-testid={`candidate-document-download-${item.id}`}><Download size={17} /></button></article>) : <div className="document-empty" data-testid="candidate-documents-empty">No documents uploaded yet.</div>}</section>
        </div>
      </section>
    </main>
  );
}